<!--
// app/components/pages/mercato/MarketSection.vue
-->
<script setup lang="ts">
import { setAliasUrl } from "~~/src/Shared/utils";
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import type { MercatoTransfer } from "~~/src/mercato/domain/models/MercatoTransfer";
import BaseModal from "~/components/base/BaseModal.vue";
import ConceptLink from "~/components/glossary/ConceptLink.vue";

type Props = {
  teams: MercatoTeam[];
  marketPlayers: MercatoPlayer[];
  marketPlayersStatus: string;
  league: string;
};

const props = defineProps<Props>();

const { $t, $tn, $getLocale } = useI18n();
const locale = $getLocale();

const { playerPlaceholderPath } = useImages();

const theTeams = props.teams.reduce(
  (acc, cur) => {
    acc[String(cur.id)] = cur;
    return acc;
  },
  {} as Record<string, MercatoTeam>
);

const leagueFilteredPLayersAmount = ref(props.marketPlayers?.length);

// Filter refs
const textSearchInput = ref("");
const textSearch = ref("");
const selectedPosition = ref<string>("");
const selectedTeam = ref<string>("");
const selectedStatus = ref<string>("");
const marketValueMin = ref(0);
const marketValueMax = ref(0);
const rangeSliderKey = ref(0);

// Player history modal state
const selectedPlayerForHistory = ref<MercatoPlayer | null>(null);
const isHistoryModalOpen = ref(false);
const playerTransfers = ref<MercatoTransfer[]>([]);
const loadingTransfers = ref(false);

// Debounce text search for performance with ~1000 players
let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
watch(textSearchInput, newValue => {
  if (debounceTimeout) clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(() => {
    textSearch.value = newValue;
  }, 300);
});

// Computed: Available filter options
const availablePositions = computed(() => {
  if (!props.marketPlayers) return [];

  // Filter players by league first (same logic as in filteredPlayers)
  const leagueFilteredPlayers = props.marketPlayers.filter(player => {
    if (props.league && props.league !== "all" && player.league !== props.league) {
      return false;
    }
    return true;
  });

  const positions = new Set(leagueFilteredPlayers.map(p => p.position).filter(Boolean));
  return Array.from(positions).sort();
});

const availableTeams = computed(() => {
  if (!props.marketPlayers) return [];

  // Filter players by league first (same logic as in filteredPlayers)
  const leagueFilteredPlayers = props.marketPlayers.filter(player => {
    if (props.league && props.league !== "all" && player.league !== props.league) {
      return false;
    }
    return true;
  });

  // Filter by undefined/null keeping 0 as valid
  const teams = new Set(
    leagueFilteredPlayers.map(p => p.teamId).filter(id => id !== undefined && id !== null && id !== "")
  );

  const teamsArray = Array.from(teams);

  // Split Free Agent (id: 0 or "0")
  const freeAgent = teamsArray.filter(id => id == 0);
  const otherTeams = teamsArray.filter(id => id != 0);

  // Order other teams alphabetically
  otherTeams.sort((a, b) => {
    const teamA = theTeams[a]?.completeName || "";
    const teamB = theTeams[b]?.completeName || "";
    return teamA.localeCompare(teamB);
  });

  return [...freeAgent, ...otherTeams];
});

const availableStatuses = computed(() => {
  if (!props.marketPlayers) return [];

  // Filter players by league first (same logic as in filteredPlayers)
  const leagueFilteredPlayers = props.marketPlayers.filter(player => {
    if (props.league && props.league !== "all" && player.league !== props.league) {
      return false;
    }
    return true;
  });

  const statuses = new Set<string>();

  // Split comma-separated statuses and add each one individually
  leagueFilteredPlayers.forEach(player => {
    if (player.status) {
      player.status.split(",").forEach(status => {
        const trimmedStatus = status.trim();
        if (trimmedStatus) {
          statuses.add(trimmedStatus);
        }
      });
    }
  });

  return Array.from(statuses).sort();
});

const marketValueMinMax = computed(() => {
  if (!props.marketPlayers || props.marketPlayers.length === 0) {
    return { min: 0, max: 100 };
  }

  // Filter players by league first (same logic as in filteredPlayers)
  const leagueFilteredPlayers = props.marketPlayers.filter(player => {
    if (props.league && props.league !== "all" && player.league !== props.league) {
      return false;
    }
    return true;
  });

  leagueFilteredPLayersAmount.value = leagueFilteredPlayers.length;

  // Calculate min/max from league-filtered players
  const values = leagueFilteredPlayers.map(p => parseFloat(p.marketValue) || 0).filter(v => !isNaN(v));

  if (values.length === 0) return { min: 0, max: 100 };

  const min = Math.floor(Math.min(...values));
  const max = Math.ceil(Math.max(...values));
  return { min, max };
});

// Initialize range when data loads
watch(
  marketValueMinMax,
  newMinMax => {
    if (marketValueMin.value === 0 && marketValueMax.value === 0) {
      marketValueMin.value = newMinMax.min;
      marketValueMax.value = newMinMax.max;
    }
  },
  { immediate: true }
);

// Reset range when league changes
watch(
  () => props.league,
  () => {
    marketValueMin.value = marketValueMinMax.value.min;
    marketValueMax.value = marketValueMinMax.value.max;
    rangeSliderKey.value++; // Force re-render of range slider
  }
);

// Reset filters when they become unavailable after league change
watch(
  () => props.league,
  () => {
    // Reset position filter if selected position is not in the new available positions
    if (selectedPosition.value && !availablePositions.value.includes(selectedPosition.value)) {
      selectedPosition.value = "";
    }

    // Reset team filter if selected team is not in the new available teams
    if (selectedTeam.value && !availableTeams.value.includes(selectedTeam.value)) {
      selectedTeam.value = "";
    }

    // Reset status filter if selected status is not in the new available statuses
    if (selectedStatus.value && !availableStatuses.value.includes(selectedStatus.value)) {
      selectedStatus.value = "";
    }
  }
);

// Computed: Filtered players
const filteredPlayers = computed(() => {
  if (!props.marketPlayers) return [];

  return props.marketPlayers.filter(player => {
    // League filter (always active based on prop)
    if (props.league && props.league !== "all" && player.league !== props.league) {
      return false;
    }

    // Text search filter
    if (textSearch.value) {
      const searchLower = textSearch.value.toLowerCase();
      const fullName = player.name.toLowerCase();
      const teamName = player.teamName?.toLowerCase() || "";
      const position = $t(player.position).toLowerCase();
      const status = $t(`mercato.status.${player.status}`)?.toLowerCase() || "";
      const marketValue = player.marketValue?.toString() || "";

      const matchesSearch =
        fullName.includes(searchLower) ||
        teamName.includes(searchLower) ||
        position.includes(searchLower) ||
        status.includes(searchLower) ||
        marketValue.includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Position filter
    if (selectedPosition.value && player.position !== selectedPosition.value) {
      return false;
    }

    // Team filter
    if (selectedTeam.value && player.teamId !== selectedTeam.value) {
      return false;
    }

    // Status filter - check if selected status is in player's status list (comma-separated)
    if (selectedStatus.value) {
      const playerStatuses = player.status?.split(",").map(s => s.trim()) || [];
      if (!playerStatuses.includes(selectedStatus.value)) {
        return false;
      }
    }

    // Market value range filter
    const playerMarketValue = parseFloat(player.marketValue) || 0;
    if (playerMarketValue < marketValueMin.value || playerMarketValue > marketValueMax.value) {
      return false;
    }

    return true;
  });
});

// Clear all filters
const clearFilters = () => {
  textSearchInput.value = "";
  textSearch.value = "";
  selectedPosition.value = "";
  selectedTeam.value = "";
  selectedStatus.value = "";
  marketValueMin.value = marketValueMinMax.value.min;
  marketValueMax.value = marketValueMinMax.value.max;
  rangeSliderKey.value++; // Force re-render of range slider
};

// Check if any filter is active
const hasActiveFilters = computed(() => {
  return (
    textSearch.value !== "" ||
    selectedPosition.value !== "" ||
    selectedTeam.value !== "" ||
    selectedStatus.value !== "" ||
    marketValueMin.value !== marketValueMinMax.value.min ||
    marketValueMax.value !== marketValueMinMax.value.max
  );
});

// Player history modal functions
const openPlayerHistoryModal = async (player: MercatoPlayer) => {
  selectedPlayerForHistory.value = player;
  isHistoryModalOpen.value = true;
  await loadPlayerTransfers(player.id);
};

const loadPlayerTransfers = async (playerId: string) => {
  loadingTransfers.value = true;
  try {
    const { data } = await useLazyAsyncData("mercatoTransfers", () =>
      $fetch<MercatoTransfer[]>("/api/v1/mercato/transfers")
    );

    // Filter transfers for this player and sort by date (most recent first)
    if (data.value) {
      playerTransfers.value = data.value
        .filter(t => t.playerId === playerId)
        .sort((a, b) => {
          // Sort by date (most recent first)
          if (!a.date || !b.date) return 0;
          return b.date.localeCompare(a.date);
        });
    } else {
      playerTransfers.value = [];
    }
  } catch (error) {
    console.error("Error loading player transfers:", error);
    playerTransfers.value = [];
  } finally {
    loadingTransfers.value = false;
  }
};

const getTeamName = (teamId: string): string => {
  return theTeams[teamId]?.completeName || "Unknown Team";
};

const getTeamShortName = (teamId: string): string => {
  return theTeams[teamId]?.shortName || "UT";
};

const getTeamLogo = (teamId: string): string | null => {
  return theTeams[teamId]?.logo?.url || null;
};

const isTransferActive = (status: string): boolean => {
  return status === "ROFR" || status === "Player Decision" || status === "Negotiation";
};

const getTransferTypeConceptId = (transferType: string): string | null => {
  const typeMap: Record<string, string> = {
    Trade: "concept-trade",
    Transfer: "concept-transfer",
    "Release Clause": "concept-release-clause",
  };
  return typeMap[transferType] || null;
};

if (props.marketPlayersStatus === "success") clearFilters();
</script>

<template>
  <section ref="market" id="market" class="kql-w-container px-4 xl:px-0">
    <div class="my-10">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        <!-- Text Search -->
        <div class="flex flex-col gap-2 lg:col-span-2">
          <label for="text-search" class="font-medium">{{ $t("shared.search.placeholder") }}</label>
          <search-field v-model="textSearchInput" light></search-field>
        </div>

        <!-- Market Value Range -->
        <div class="flex flex-col gap-2 lg:col-span-2">
          <label for="market-value-filter" class="font-medium">
            <concept-link concept="concept-market-value">{{ $t("general.marketValue") }}</concept-link>
          </label>
          <range-slider
            id="market-value-filter"
            :key="`range-${rangeSliderKey}`"
            title=""
            v-model:min="marketValueMin"
            v-model:max="marketValueMax"
            :from="marketValueMinMax.min"
            :to="marketValueMinMax.max"
            class="mercato"
          />
        </div>

        <!-- Position Filter -->
        <div class="flex flex-col gap-2">
          <label for="position-filter" class="font-medium">{{ $t("draft.player.position") }}</label>
          <select
            id="position-filter"
            v-model="selectedPosition"
            class="kql-select text-xs lg:text-sm border-l rounded-lg w-full"
          >
            <option value="">{{ $t("general.all.female") }}</option>
            <option v-for="position in availablePositions" :key="position" :value="position">
              {{ $t(position) }}
            </option>
          </select>
        </div>

        <!-- Team Filter -->
        <div class="flex flex-col gap-2">
          <label for="team-filter" class="font-medium"
            >{{ $t("stats.table.header.team") }} /
            <concept-link concept="concept-free-agent"> Free Agent </concept-link>
          </label>
          <select
            id="team-filter"
            v-model="selectedTeam"
            class="kql-select text-xs lg:text-sm border-l rounded-lg w-full"
          >
            <option value="">{{ $t("general.all.male") }}</option>
            <option v-for="teamId in availableTeams" :key="teamId" :value="teamId">
              {{ getTeamName(teamId) || teamId }}
            </option>
          </select>
        </div>

        <!-- Status Filter -->
        <div class="flex flex-col gap-2">
          <label for="status-filter" class="font-medium">
            <concept-link concept="concept-available" tooltip="Player Status">Status</concept-link>
          </label>
          <select
            id="status-filter"
            v-model="selectedStatus"
            class="kql-select text-xs lg:text-sm border-l rounded-lg w-full"
          >
            <option value="">{{ $t("general.all.male") }}</option>
            <option v-for="status in availableStatuses" :key="status" :value="status">
              {{ ["Locked", "Secured"].includes(status) ? $t(`mercato.${status}`) : status }}
            </option>
          </select>
        </div>

        <!-- Clear Filters Button -->
        <div class="flex flex-col justify-end">
          <button
            type="button"
            :disabled="!hasActiveFilters"
            @click="clearFilters"
            class="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ $t("draft.player.filters.clean") }}
          </button>
        </div>
      </div>

      <!-- Results Count -->
      <div class="mt-4 text-center">
        <p class="text-sm text-gray-400">
          {{
            $t("mercato.table.resultCount", {
              count: filteredPlayers.length,
              total: leagueFilteredPLayersAmount || 0,
            })
          }}
        </p>
      </div>
    </div>

    <div class="table--market-players">
      <PrimeDataTable
        :value="filteredPlayers"
        paginator
        paginator-position="bottom"
        :rows="10"
        :rows-per-page-options="[10, 20, 40]"
        scrollHeight="flex"
        table-class="min-w-max"
        removable-sort
      >
        <PrimeColumn
          field="name"
          sortable
          :header="$t('general.player.word')"
          style="width: 35%; padding: 0 2px"
        >
          <template #body="slotProps">
            <div class="flex items-center justify-start py-2 leading-tight">
              <NuxtImg
                loading="lazy"
                :src="setAliasUrl(slotProps.data.imageUrl) || playerPlaceholderPath"
                :alt="slotProps.data.name"
                class="rounded-full me-2 shrink-0 bg-gray-700 object-top object-cover w-10 h-10"
                width="40"
                height="40"
                sizes="40px"
                fit="outside"
              />
              <p class="text-sm md:text-base md:leading-none">
                {{ slotProps.data.name }} <br />
                <span class="text-xs md:text-sm text-gray-600">{{ $t(slotProps.data.position) }}</span>
              </p>
            </div>
          </template>
        </PrimeColumn>
        <PrimeColumn field="teamName" sortable style="width: 25%; padding: 0 2px">
          <template #header>
            <span class="p-datatable-column-title"
              >{{ $t("general.currentTeam") }} <br class="md:hidden" />
              / Free agents</span
            >
          </template>
          <template #body="slotProps">
            <div class="flex items-center justify-start w-full gap-1">
              <NuxtImg
                v-if="slotProps && slotProps.data.teamId && theTeams[slotProps.data.teamId]?.logo?.url"
                loading="lazy"
                :src="setAliasUrl(getTeamLogo(slotProps.data.teamId) || '')"
                :alt="getTeamName(slotProps.data.teamId) || ''"
                class="team-logo"
                width="35"
                height="35"
                sizes="35px"
              />
              <p
                :class="{ 'font-semibold': slotProps.data.teamId == 0 }"
                class="text-xs md:text-sm max-w-[75%] md:max-w-full whitespace-normal"
              >
                {{ getTeamName(slotProps.data.teamId) }}
              </p>
              <button
                v-if="slotProps.data.hasHistory === 'TRUE'"
                class="text-mercato hover:text-white flex items-center justify-start text-xs gap-0.5"
                @click="openPlayerHistoryModal(slotProps.data)"
              >
                <svg
                  class="w-4 h-4 bigsm:w-3 bigsm:h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3M3.22302 14C4.13247 18.008 7.71683 21 12 21c4.9706 0 9-4.0294 9-9 0-4.97056-4.0294-9-9-9-3.72916 0-6.92858 2.26806-8.29409 5.5M7 9H3V5"
                  />
                </svg>
                <span class="hidden bigsm:inline">History</span>
              </button>
            </div>
          </template>
        </PrimeColumn>
        <PrimeColumn field="marketValue" sortable style="padding: 0 2px">
          <template #header>
            <span class="p-datatable-column-title"
              >Market <br class="md:hidden" />
              Value</span
            >
          </template>
          <template #body="slotProps">
            <p class="uppercase text-sm md:text-base md:leading-none" v-if="slotProps.data.teamId == 0">
              free
            </p>
            <p v-else class="text-sm md:text-base md:leading-none">
              {{ $tn(Number(slotProps.data.marketValue)) }} M
            </p>
          </template>
        </PrimeColumn>
        <PrimeColumn field="status" header="Status" style="width: 15%; padding: 0 2px">
          <template #body="slotProps">
            <div v-if="slotProps.data.status" class="flex items-start justify-start flex-wrap gap-1">
              <span
                v-for="status in slotProps.data.status.split(',')"
                class="bg-gray-600 rounded-lg text-xs uppercase px-2 py-1 max-w-max"
                >{{
                  ["Locked", "Secured"].includes(status.trim())
                    ? $t(`mercato.${status.trim()}`)
                    : status.trim()
                }}</span
              >
            </div>
          </template>
        </PrimeColumn>
        <PrimeColumn field="interest" :header="$t('mercato.interest')" style="padding: 0 2px">
          <template #body="slotProps">
            <div
              v-if="slotProps && slotProps.data?.interest"
              class="flex items-center justify-start w-full flex-nowrap"
            >
              <div
                v-for="(team, i) in slotProps.data.interest.split(',')"
                :class="
                  i > 0 && slotProps.data.interest.split(',').length > 2
                    ? '-ms-3 p-0.5 bg-black rounded-full'
                    : 'p-0.5'
                "
              >
                <NuxtImg
                  v-if="theTeams[team.trim()]?.logo?.url"
                  loading="lazy"
                  :src="setAliasUrl(theTeams[team.trim()]?.logo?.url) || ''"
                  :alt="theTeams[team.trim()]?.completeName || ''"
                  width="35"
                  height="35"
                  sizes="35px"
                />
              </div>
            </div>
          </template>
        </PrimeColumn>
        <template #empty>
          <div class="py-2 ms-4">{{ $t("shared.filters.noData") }}</div>
        </template>
      </PrimeDataTable>
    </div>

    <!-- Player Transfer History Modal -->
    <BaseModal modal-id="playerHistoryModal" v-model="isHistoryModalOpen" show-close-button>
      <!-- Modal Header -->
      <template #header>
        <div v-if="selectedPlayerForHistory" class="player-history-modal__header">
          <div class="flex items-center gap-4 p-4">
            <NuxtImg
              :src="setAliasUrl(selectedPlayerForHistory.imageUrl) || playerPlaceholderPath"
              :alt="selectedPlayerForHistory.name"
              width="50"
              height="50"
              sizes="50px"
              class="rounded-full bg-gray-700 object-cover object-top w-12 h-12"
              fit="outside"
            />
            <div>
              <p class="text-lg font-bold">{{ selectedPlayerForHistory.name }}</p>
              <p class="text-gray-400">{{ $t("mercato.transferHistory") }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- Modal Body -->
      <template #body>
        <!-- Loading State -->
        <div v-if="loadingTransfers" class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-mercato"></div>
        </div>

        <!-- Transfers List -->
        <div v-else-if="playerTransfers.length > 0" class="player-history-modal__transfers">
          <div
            v-for="(transfer, index) in playerTransfers"
            :key="`${transfer.playerId}-${index}`"
            class="transfer-card"
          >
            <div class="transfer-card__info">
              <nuxt-time
                :datetime="transfer.date"
                day="2-digit"
                month="2-digit"
                year="numeric"
                :locale="locale"
              ></nuxt-time>
            </div>

            <div class="bg-gray-800 rounded-lg p-2">
              <div class="flex items-center justify-start gap-3">
                <concept-link
                  v-if="transfer.transferType"
                  :concept="getTransferTypeConceptId(transfer.transferType)!"
                  class="transfer-card__badge"
                >
                  {{ $t(`mercato.${transfer.transferType}`) }}
                </concept-link>
                <div v-if="transfer.paid && transfer.paid !== '0'" class="text-center">
                  <span class="transfer-card__paid">{{ $tn(Number(transfer.paid)) }} M</span>
                </div>

                <div
                  v-if="transfer.status && isTransferActive(transfer.status)"
                  class="transfer-card__status"
                >
                  <concept-link
                    :concept="
                      transfer.status === 'ROFR'
                        ? 'concept-rofr'
                        : transfer.status === 'Negotiation'
                          ? 'concept-negotiation'
                          : 'concept-player-decision'
                    "
                  >
                    {{ transfer.status }}
                  </concept-link>
                </div>
              </div>

              <div class="flex items-center justify-start w-full gap-2 mt-2">
                <!-- From Team -->
                <div class="transfer-card__team">
                  <NuxtImg
                    v-if="getTeamLogo(transfer.oldTeamId)"
                    :src="setAliasUrl(getTeamLogo(transfer.oldTeamId)!)"
                    :alt="getTeamName(transfer.oldTeamId)"
                    width="40"
                    height="40"
                    sizes="40px"
                    class="transfer-card__logo"
                  />
                  <div v-else class="transfer-card__logo-placeholder"></div>
                  <p class="transfer-card__team-name hidden sm:block">
                    {{ getTeamName(transfer.oldTeamId) }}
                  </p>
                  <p class="transfer-card__team-name sm:hidden">{{ getTeamShortName(transfer.oldTeamId) }}</p>
                </div>

                <!-- Arrow -->
                <div class="transfer-card__arrow">
                  <svg
                    class="w-6 h-6 text-mercato"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      :stroke-dasharray="isTransferActive(transfer.status) ? '3 5' : undefined"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    ></path>
                  </svg>
                </div>

                <!-- To Team -->
                <div class="transfer-card__team">
                  <NuxtImg
                    v-if="getTeamLogo(transfer.newTeamId)"
                    :src="setAliasUrl(getTeamLogo(transfer.newTeamId)!)"
                    :alt="getTeamName(transfer.newTeamId)"
                    width="40"
                    height="40"
                    sizes="40px"
                    class="transfer-card__logo"
                  />
                  <div v-else class="transfer-card__logo-placeholder"></div>
                  <p class="transfer-card__team-name hidden sm:block">
                    {{ getTeamName(transfer.newTeamId) }}
                  </p>
                  <p class="transfer-card__team-name sm:hidden">{{ getTeamShortName(transfer.newTeamId) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-8 text-gray-400">
          <p>{{ $t("mercato.noTransfersFound") }}</p>
        </div>
      </template>
    </BaseModal>
  </section>
</template>

<style scoped>
/* Player History Modal */
.player-history-modal__header {
  @apply pb-4 border-b border-gray-700;
}

.player-history-modal__transfers {
  @apply flex flex-col gap-4 max-h-[60vh] overflow-y-auto;
}

/* Transfer Card */
.transfer-card {
  @apply flex flex-col gap-2 min-w-max;
}

.transfer-card__team {
  @apply flex items-center gap-1.5;
}

.transfer-card__logo {
  @apply rounded-lg flex-shrink-0;
}

.transfer-card__logo-placeholder {
  @apply w-10 h-10 bg-gray-600 rounded-lg flex-shrink-0;
}

.transfer-card__team-name {
  @apply font-semibold;
}

.transfer-card__arrow {
  @apply flex items-center justify-center;
}

.transfer-card__info {
  @apply text-gray-400;
}

.transfer-card__paid {
  @apply text-mercato font-semibold;
}

.transfer-card__badge {
  @apply bg-gray-700 text-gray-200 px-2 py-0.5 rounded text-[10px] font-semibold uppercase;
}

.transfer-card__status {
  @apply bg-orange-700 bg-opacity-90 text-orange-100 px-2 py-0.5 rounded text-[9px] uppercase font-bold whitespace-nowrap ms-auto;
}
</style>
